// YouVersion Platform API client — official, licensed Bible text (including
// NIV) via https://developers.youversion.com. Requests go through
// /api/bible (a Vercel serverless function) so the App Key stays
// server-side; see api/bible.js.
import { supabase } from './supabase';

// SleekHear version codes mapped to the localized_abbreviation values that
// this YouVersion app key actually carries (confirmed against the live
// /bibles catalog). Versions the key doesn't license (KJV, ESV, NLT, NKJV,
// CSB, MSG) are intentionally omitted — bibleApi.js routes those to its
// public-domain fallback instead of a doomed YouVersion round-trip.
// The numeric IDs the API requires are resolved at runtime from the catalog.
export const YOUVERSION_ABBREVIATIONS = {
  ASV: 'ASV',
  WEB: 'WEBUS',
  NIV: 'NIV',
  NIVUK: 'NIVUK',
  NASB: 'NASB2020',
  AMP: 'AMP',
  TPT: 'TPT',
};

// USFM book codes for the 66 Protestant canon books, keyed by the common
// English names/abbreviations SleekHear's verse detection produces.
const BOOK_CODES = {
  genesis: 'GEN', exodus: 'EXO', leviticus: 'LEV', numbers: 'NUM', deuteronomy: 'DEU',
  joshua: 'JOS', judges: 'JDG', ruth: 'RUT',
  '1samuel': '1SA', '2samuel': '2SA', '1kings': '1KI', '2kings': '2KI',
  '1chronicles': '1CH', '2chronicles': '2CH', ezra: 'EZR', nehemiah: 'NEH', esther: 'EST',
  job: 'JOB', psalm: 'PSA', psalms: 'PSA', proverbs: 'PRO', ecclesiastes: 'ECC',
  song: 'SNG', songofsolomon: 'SNG', songofsongs: 'SNG',
  isaiah: 'ISA', jeremiah: 'JER', lamentations: 'LAM', ezekiel: 'EZK', daniel: 'DAN',
  hosea: 'HOS', joel: 'JOL', amos: 'AMO', obadiah: 'OBA', jonah: 'JON', micah: 'MIC',
  nahum: 'NAM', habakkuk: 'HAB', zephaniah: 'ZEP', haggai: 'HAG', zechariah: 'ZEC', malachi: 'MAL',
  matthew: 'MAT', mark: 'MRK', luke: 'LUK', john: 'JHN', acts: 'ACT',
  romans: 'ROM', '1corinthians': '1CO', '2corinthians': '2CO', galatians: 'GAL',
  ephesians: 'EPH', philippians: 'PHP', colossians: 'COL',
  '1thessalonians': '1TH', '2thessalonians': '2TH', '1timothy': '1TI', '2timothy': '2TI',
  titus: 'TIT', philemon: 'PHM', hebrews: 'HEB', james: 'JAS',
  '1peter': '1PE', '2peter': '2PE', '1john': '1JN', '2john': '2JN', '3john': '3JN',
  jude: 'JUD', revelation: 'REV',
};

function normalizeBookKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Convert a free-text reference like "John 3:16" or "1 Corinthians 13:4-7"
 * into YouVersion's USFM reference format, e.g. "JHN.3.16" or "1CO.13.4".
 * Verse ranges are collapsed to their starting verse (the passages endpoint
 * takes a single verse or verse range per the BOOK.CHAPTER.VERSE pattern).
 * Returns null if the book name isn't recognized.
 */
export function toUsfmReference(reference) {
  const match = reference.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  const [, bookName, chapter, verse, verseEnd] = match;
  const bookCode = BOOK_CODES[normalizeBookKey(bookName)];
  if (!bookCode) return null;
  return verseEnd ? `${bookCode}.${chapter}.${verse}-${verseEnd}` : `${bookCode}.${chapter}.${verse}`;
}

let versionCatalogPromise = null;

async function callBibleProxy(params) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('You must be signed in to use YouVersion');
  }
  const query = new URLSearchParams(params).toString();
  const response = await fetch(`/api/bible?${query}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `YouVersion API error: ${response.status}`);
  }
  return data;
}

/**
 * Look up the numeric version ID for a given YouVersion abbreviation
 * (e.g. "NIV"), fetched once from the catalog and cached for the session.
 */
async function resolveVersionId(abbreviation) {
  if (!versionCatalogPromise) {
    versionCatalogPromise = callBibleProxy({ list: 'bibles' }).catch((err) => {
      versionCatalogPromise = null;
      throw err;
    });
  }
  const catalog = await versionCatalogPromise;
  const bibles = catalog.data || catalog.bibles || catalog.items || [];
  const target = abbreviation.toUpperCase();
  const entry = bibles.find((b) =>
    (b.localized_abbreviation || '').toUpperCase() === target ||
    (b.abbreviation || '').toUpperCase() === target
  );
  if (!entry) {
    throw new Error(`YouVersion does not carry version "${abbreviation}"`);
  }
  return entry.id;
}

/**
 * Fetch verse text from the official YouVersion API.
 * @param {string} reference - Free-text reference, e.g. "John 3:16"
 * @param {string} version - SleekHear version code, e.g. "NIV"
 * @returns {Promise<{success: boolean, text?: string, error?: string}>}
 */
export async function fetchYouVersionVerse(reference, version) {
  const abbreviation = YOUVERSION_ABBREVIATIONS[version];
  if (!abbreviation) {
    return { success: false, error: `${version} is not mapped to a YouVersion abbreviation` };
  }
  const usfmReference = toUsfmReference(reference);
  if (!usfmReference) {
    return { success: false, error: `Could not parse reference "${reference}"` };
  }

  try {
    const versionId = await resolveVersionId(abbreviation);
    const data = await callBibleProxy({ versionId, reference: usfmReference });
    const raw = data.content || data.text || '';
    if (!raw) {
      return { success: false, error: 'No text returned from YouVersion' };
    }
    // Strip any residual HTML tags (the API can still wrap content in
    // <p>/<span> markup) and collapse whitespace.
    const text = raw
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) {
      return { success: false, error: 'No text returned from YouVersion' };
    }
    return { success: true, text, reference };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

const youVersionApi = { fetchYouVersionVerse, toUsfmReference };
export default youVersionApi;
